// Custom Report Builder. Three-panel layout per docs/04:
//   1. Column selector (group → checkbox; locked-on display_name + matching balance)
//   2. Filter builder (mirror of accounts list filters)
//   3. Balance source picker
// Live preview at the bottom. Buttons: Download CSV / Save as Template / Save Changes.
//
// `?template=<id>` opens the builder pre-filled with that saved template.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import * as XLSX from 'xlsx';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ApiError, reports } from '@/lib/api';
import { formatBalance, formatTransaction } from '@/lib/format';
import type {
  CustomReportColumn,
  CustomReportFilters,
  CustomReportPreviewRequest,
  CustomReportPreviewResponse,
  CustomReportSort,
  ReportBalanceSource,
} from '@frt/shared-types';

const REQUIRED_COL = 'display_name';
const BALANCE_VARIANT_BY_SOURCE: Record<ReportBalanceSource, string> = {
  current: 'balance_current',
  last_month_end: 'balance_last_month_end',
  as_of_period: 'balance_as_of_period',
};

function fmt(value: unknown, type: string, columnId: string): string {
  if (value === null || value === undefined) return '';
  if (type === 'currency') {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    // Balance columns are whole-dollar; per-account fees show cents.
    if (columnId.startsWith('balance_')) return formatBalance(n);
    return formatTransaction(n);
  }
  if (type === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

interface BuilderState {
  columns: string[];
  filters: CustomReportFilters;
  sort: CustomReportSort | null;
  balance_source: ReportBalanceSource;
  default_as_of_period: string | null;
}

type PivotAggregate = 'sum' | 'count' | 'avg' | 'min' | 'max';

interface PivotConfig {
  /** Ordered list of column ids used as the Rows axis. Multiple = nested
   *  groupings shown as separate header columns (Excel "Tabular Form"). */
  rowFields: string[];
  /** Ordered list of column ids used as the Columns axis. Multiple =
   *  combined column keys (e.g. "RF / active"). */
  colFields: string[];
  /** Numeric/currency column id whose values get aggregated. Required for
   *  every aggregate except `count`. */
  valueField: string;
  aggregate: PivotAggregate;
  /** Per-attribute include lists. Key = column id; value = array of
   *  allowed string-stringified values. Empty array means the attribute
   *  is listed but unrestricted (treated as no filter). */
  filters: Record<string, string[]>;
}

function defaultState(): BuilderState {
  return {
    columns: ['display_name', 'customer_code', 'tester_name', 'product_type', 'balance_current'],
    filters: {},
    sort: null,
    balance_source: 'current',
    default_as_of_period: null,
  };
}

function defaultPivot(): PivotConfig {
  return {
    rowFields: ['customer_code'],
    colFields: [],
    valueField: 'balance_current',
    aggregate: 'sum',
    filters: {},
  };
}

export function CustomBuilderPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const initialTemplateId = (() => {
    const usp = new URLSearchParams(router.state.location.search);
    const id = usp.get('template');
    return id ? Number(id) : null;
  })();

  const [templateId, setTemplateId] = useState<number | null>(initialTemplateId);
  const [state, setState] = useState<BuilderState>(defaultState);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'flat' | 'pivot'>('flat');
  const [pivot, setPivot] = useState<PivotConfig>(defaultPivot);

  const cols = useQuery({ queryKey: ['reports', 'custom', 'columns'], queryFn: () => reports.customColumns() });

  // If we have a template id, fetch + hydrate.
  const tpl = useQuery({
    queryKey: ['reports', 'template', templateId],
    queryFn: () => reports.templates.get(templateId!),
    enabled: templateId !== null,
  });

  useEffect(() => {
    if (tpl.data) {
      setState({
        columns: tpl.data.columns,
        filters: tpl.data.filters ?? {},
        sort: tpl.data.sort,
        balance_source: tpl.data.balance_source,
        default_as_of_period: tpl.data.default_as_of_period,
      });
      // If the template was saved in pivot mode, restore the config and
      // switch the page into Pivot view automatically.
      const tplPivot = tpl.data.pivot as PivotConfig | null | undefined;
      if (tplPivot && typeof tplPivot === 'object') {
        setPivot({
          rowFields: tplPivot.rowFields ?? [],
          colFields: tplPivot.colFields ?? [],
          valueField: tplPivot.valueField ?? '',
          aggregate: tplPivot.aggregate ?? 'sum',
          filters: tplPivot.filters ?? {},
        });
        setViewMode('pivot');
      } else {
        setViewMode('flat');
      }
    }
  }, [tpl.data]);

  // Enforce locked-on columns whenever balance_source changes.
  useEffect(() => {
    setState((prev) => {
      const required = BALANCE_VARIANT_BY_SOURCE[prev.balance_source];
      const otherBalances = Object.values(BALANCE_VARIANT_BY_SOURCE).filter((id) => id !== required);
      let next = prev.columns.filter((c) => !otherBalances.includes(c));
      if (!next.includes(REQUIRED_COL)) next = [REQUIRED_COL, ...next];
      if (!next.includes(required)) next = [...next, required];
      if (
        next.length === prev.columns.length &&
        next.every((c, i) => c === prev.columns[i])
      ) {
        return prev;
      }
      return { ...prev, columns: next };
    });
  }, [state.balance_source]);

  // In flat mode we fetch a small page (25 rows is enough for the preview
  // table). In pivot mode we need the full result set so the in-page
  // aggregation reflects every account, capped at the API's 500-row max.
  const previewBody: CustomReportPreviewRequest = useMemo(
    () => ({
      columns: state.columns,
      filters: state.filters,
      sort: state.sort,
      balance_source: state.balance_source,
      default_as_of_period: state.balance_source === 'as_of_period' ? state.default_as_of_period : null,
      page: 1,
      per_page: viewMode === 'pivot' ? 500 : 25,
    }),
    [state, viewMode],
  );

  const preview = useQuery<CustomReportPreviewResponse, Error>({
    queryKey: ['reports', 'custom', 'preview', previewBody],
    queryFn: () => reports.customPreview(previewBody),
    retry: false,
  });

  const onDownload = async () => {
    try {
      await reports.customExport(previewBody);
    } catch (err) {
      console.error(err);
    }
  };

  const onExportPivot = () => {
    if (!preview.data) return;
    const cols = preview.data.columns;
    const rowCols = pivot.rowFields
      .map((id) => cols.find((c) => c.id === id))
      .filter((c): c is CustomReportColumn => Boolean(c));
    const colCols = pivot.colFields
      .map((id) => cols.find((c) => c.id === id))
      .filter((c): c is CustomReportColumn => Boolean(c));
    const valueCol = cols.find((c) => c.id === pivot.valueField);
    const aoa = pivotToAoa({
      data: preview.data,
      config: pivot,
      rowCols,
      colCols,
      valueCol,
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pivot');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `custom-report-pivot_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const saveAs = useMutation({
    mutationFn: async () => {
      const name = window.prompt('Template name:');
      if (!name) throw new Error('cancelled');
      const description = window.prompt('Description (optional):') ?? null;
      return reports.templates.create({
        name,
        description,
        columns: state.columns,
        filters: state.filters,
        sort: state.sort,
        balance_source: state.balance_source,
        default_as_of_period:
          state.balance_source === 'as_of_period' ? state.default_as_of_period : null,
        // Persist the Pivot Setup snapshot when the template is being saved
        // from pivot mode; otherwise null = "open in flat view".
        pivot: viewMode === 'pivot' ? pivot : null,
      });
    },
    onSuccess: async (created) => {
      setTemplateId(created.id);
      setSaveError(null);
      await queryClient.invalidateQueries({ queryKey: ['reports', 'templates'] });
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'cancelled') return;
      setSaveError(err instanceof ApiError ? err.message : String(err));
    },
  });

  const saveChanges = useMutation({
    mutationFn: async () => {
      if (templateId === null) throw new Error('not loaded from a template');
      return reports.templates.update(templateId, {
        columns: state.columns,
        filters: state.filters,
        sort: state.sort,
        balance_source: state.balance_source,
        default_as_of_period:
          state.balance_source === 'as_of_period' ? state.default_as_of_period : null,
        pivot: viewMode === 'pivot' ? pivot : null,
      });
    },
    onSuccess: async () => {
      setSaveError(null);
      await queryClient.invalidateQueries({ queryKey: ['reports', 'templates'] });
      await queryClient.invalidateQueries({ queryKey: ['reports', 'template', templateId] });
    },
    onError: (err) => {
      setSaveError(err instanceof ApiError ? err.message : String(err));
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">
          Custom Report Builder
          {templateId !== null && tpl.data && (
            <span className="ml-2 text-base text-slate-500">— {tpl.data.name}</span>
          )}
        </h1>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded border border-slate-300 text-xs">
            <button
              type="button"
              onClick={() => setViewMode('flat')}
              className={`px-3 py-1 ${
                viewMode === 'flat' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'
              }`}
            >
              Flat
            </button>
            <button
              type="button"
              onClick={() => setViewMode('pivot')}
              className={`px-3 py-1 ${
                viewMode === 'pivot' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'
              }`}
            >
              Pivot
            </button>
          </div>
          <Button size="sm" variant="secondary" onClick={onDownload} disabled={preview.isPending}>
            Download CSV
          </Button>
          {viewMode === 'pivot' && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onExportPivot}
              disabled={!preview.data}
            >
              Export Pivot XLSX
            </Button>
          )}
          <Button size="sm" variant="primary" onClick={() => saveAs.mutate()} disabled={saveAs.isPending}>
            Save as Template
          </Button>
          {templateId !== null && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => saveChanges.mutate()}
              disabled={saveChanges.isPending}
            >
              Save Changes
            </Button>
          )}
        </div>
      </div>
      {saveError && <div className="text-sm text-red-600">{saveError}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[18rem_18rem_1fr] gap-4">
        <ColumnSelector
          all={cols.data?.columns ?? []}
          state={state}
          onChange={(columns) => setState((s) => ({ ...s, columns }))}
        />
        <FilterBuilder
          state={state.filters}
          onChange={(filters) => setState((s) => ({ ...s, filters }))}
        />
        <BalanceSourcePicker state={state} onChange={(s) => setState(s)} />
      </div>

      {viewMode === 'pivot' && preview.data && (
        <PivotConfigPanel
          catalogColumns={cols.data?.columns ?? []}
          selectedColumnIds={state.columns}
          onAddColumn={(id) =>
            setState((s) =>
              s.columns.includes(id) ? s : { ...s, columns: [...s.columns, id] },
            )
          }
          data={preview.data}
          config={pivot}
          onChange={setPivot}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>{viewMode === 'pivot' ? 'Pivot' : 'Preview'}</CardTitle>
        </CardHeader>
        <CardBody className="overflow-auto">
          {preview.isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : preview.error ? (
            <div className="text-sm text-red-600">{preview.error.message}</div>
          ) : preview.data ? (
            <>
              <div className="mb-2 text-xs text-slate-500">
                {preview.data.total} row(s){' '}
                {preview.data.balance_period && (
                  <span>· balances @ {preview.data.balance_period}</span>
                )}
                {viewMode === 'pivot' && preview.data.total > preview.data.data.length && (
                  <span className="ml-2 text-amber-700">
                    · pivot covers the first {preview.data.data.length} rows (per-page cap)
                  </span>
                )}
              </div>
              {viewMode === 'flat' ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-slate-500">
                      {preview.data.columns.map((c) => (
                        <th key={c.id} className="py-1 px-2 text-left whitespace-nowrap">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.data.data.map((row, i) => (
                      <tr key={i}>
                        {preview.data!.columns.map((c) => (
                          <td key={c.id} className="py-1 px-2 align-top">
                            {fmt(row[c.id], c.type, c.id)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <PivotTable data={preview.data} config={pivot} />
              )}
            </>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pivot config panel + pivot table renderer
// ---------------------------------------------------------------------------

function PivotConfigPanel({
  catalogColumns,
  selectedColumnIds,
  onAddColumn,
  data,
  config,
  onChange,
}: {
  /** Full attribute catalog from /reports/custom/columns. Pivot picks any
   *  field; the parent auto-adds it to the report's column selection so
   *  the next preview fetch projects it. */
  catalogColumns: CustomReportColumn[];
  selectedColumnIds: string[];
  onAddColumn: (id: string) => void;
  /** Loaded preview rows — used to enumerate distinct values for each
   *  filter attribute. */
  data: CustomReportPreviewResponse;
  config: PivotConfig;
  onChange: (next: PivotConfig) => void;
}) {
  // Hide the OTHER balance variants from the catalog so the picker doesn't
  // suggest a column that conflicts with the current balance_source.
  const usableCatalog = useMemo(
    () =>
      catalogColumns.filter(
        (c) => !c.balance_variant || selectedColumnIds.includes(c.id),
      ),
    [catalogColumns, selectedColumnIds],
  );
  const dimensionCols = useMemo(
    () =>
      usableCatalog.filter(
        (c) => c.type === 'string' || c.type === 'enum' || c.type === 'date' || c.type === 'boolean',
      ),
    [usableCatalog],
  );
  const valueCols = useMemo(
    () => usableCatalog.filter((c) => c.type === 'number' || c.type === 'currency'),
    [usableCatalog],
  );

  // Wraps an onChange to also auto-add any newly-picked field to the
  // report's column selection so the next preview projects it.
  const ensureColumns = (ids: string[]) => {
    for (const id of ids) onAddColumn(id);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pivot Setup</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldChipList
            label="Rows (in nesting order)"
            available={dimensionCols}
            selected={config.rowFields}
            onChange={(rowFields) => {
              ensureColumns(rowFields);
              onChange({ ...config, rowFields });
            }}
          />
          <FieldChipList
            label="Columns"
            available={dimensionCols}
            selected={config.colFields}
            onChange={(colFields) => {
              ensureColumns(colFields);
              onChange({ ...config, colFields });
            }}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-slate-600">Values</span>
            <select
              className="w-full rounded border border-slate-300 bg-white px-2 py-1.5"
              value={config.valueField}
              onChange={(e) => {
                if (e.target.value) onAddColumn(e.target.value);
                onChange({ ...config, valueField: e.target.value });
              }}
              disabled={config.aggregate === 'count'}
            >
              {valueCols.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-slate-600">Aggregate</span>
            <select
              className="w-full rounded border border-slate-300 bg-white px-2 py-1.5"
              value={config.aggregate}
              onChange={(e) =>
                onChange({ ...config, aggregate: e.target.value as PivotAggregate })
              }
            >
              <option value="sum">Sum</option>
              <option value="count">Count</option>
              <option value="avg">Average</option>
              <option value="min">Min</option>
              <option value="max">Max</option>
            </select>
          </label>
        </div>

        <PivotFilterBuilder
          dimensionCols={dimensionCols}
          data={data}
          config={config}
          onChange={onChange}
          onAddColumn={onAddColumn}
        />
      </CardBody>
    </Card>
  );
}

/** Multi-select chip list — picks columns from `available` into `selected`,
 *  in user-controlled order. Click a chip's × to remove; use the dropdown
 *  on the right to append more. */
function FieldChipList({
  label,
  available,
  selected,
  onChange,
}: {
  label: string;
  available: CustomReportColumn[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const remaining = available.filter((c) => !selected.includes(c.id));
  return (
    <div>
      <span className="block text-slate-600 mb-1">{label}</span>
      <div className="rounded border border-slate-300 bg-white px-2 py-1.5 flex flex-wrap items-center gap-1 min-h-[2.25rem]">
        {selected.map((id, idx) => {
          const col = available.find((c) => c.id === id);
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs"
            >
              {col?.label ?? id}
              {idx > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const next = [...selected];
                    [next[idx - 1], next[idx]] = [next[idx]!, next[idx - 1]!];
                    onChange(next);
                  }}
                  className="text-slate-400 hover:text-slate-700"
                  title="Move up"
                >
                  ←
                </button>
              )}
              {idx < selected.length - 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const next = [...selected];
                    [next[idx], next[idx + 1]] = [next[idx + 1]!, next[idx]!];
                    onChange(next);
                  }}
                  className="text-slate-400 hover:text-slate-700"
                  title="Move down"
                >
                  →
                </button>
              )}
              <button
                type="button"
                onClick={() => onChange(selected.filter((s) => s !== id))}
                className="text-red-600"
                title="Remove"
              >
                ×
              </button>
            </span>
          );
        })}
        {remaining.length > 0 && (
          <select
            className="ml-auto rounded border border-slate-200 bg-white px-1 py-0.5 text-xs"
            value=""
            onChange={(e) => {
              if (e.target.value) onChange([...selected, e.target.value]);
            }}
          >
            <option value="">+ Add</option>
            {remaining.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

/** Multi-attribute filter section. Each row picks an attribute and a
 *  set of allowed string-stringified values from the loaded data. Rows
 *  whose active-filter values don't all match are excluded from
 *  aggregation. Uses the preview rows themselves to enumerate distinct
 *  values per attribute. */
function PivotFilterBuilder({
  dimensionCols,
  data,
  config,
  onChange,
  onAddColumn,
}: {
  dimensionCols: CustomReportColumn[];
  data: CustomReportPreviewResponse;
  config: PivotConfig;
  onChange: (next: PivotConfig) => void;
  onAddColumn: (id: string) => void;
}) {
  const filterFields = Object.keys(config.filters);
  const remaining = dimensionCols.filter((c) => !filterFields.includes(c.id));

  const distinct = useMemo(() => {
    const out = new Map<string, string[]>();
    for (const f of filterFields) {
      const set = new Set<string>();
      for (const r of data.data) {
        const v = r[f];
        set.add(v == null ? '—' : String(v));
      }
      out.set(f, Array.from(set).sort((a, b) => a.localeCompare(b)));
    }
    return out;
  }, [filterFields, data.data]);

  const setField = (field: string, values: string[]) => {
    onChange({ ...config, filters: { ...config.filters, [field]: values } });
  };
  const removeField = (field: string) => {
    const next = { ...config.filters };
    delete next[field];
    onChange({ ...config, filters: next });
  };
  const addField = (field: string) => {
    onAddColumn(field);
    onChange({ ...config, filters: { ...config.filters, [field]: [] } });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="block text-slate-600">Filters</span>
        {remaining.length > 0 && (
          <select
            className="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
            value=""
            onChange={(e) => {
              if (e.target.value) addField(e.target.value);
            }}
          >
            <option value="">+ Add filter</option>
            {remaining.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        )}
      </div>
      {filterFields.length === 0 ? (
        <p className="text-xs text-slate-500">
          No filters. Add one above to scope the pivot.
        </p>
      ) : (
        <div className="space-y-2">
          {filterFields.map((field) => {
            const col = dimensionCols.find((c) => c.id === field);
            const allValues = distinct.get(field) ?? [];
            const selected = config.filters[field] ?? [];
            const remainingValues = allValues.filter((v) => !selected.includes(v));
            return (
              <div
                key={field}
                className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 flex flex-wrap items-center gap-1"
              >
                <span className="font-medium text-slate-700 mr-2">
                  {col?.label ?? field}:
                </span>
                {selected.length === 0 && (
                  <span className="text-xs text-slate-500 italic mr-1">
                    (any — pick values to restrict)
                  </span>
                )}
                {selected.map((v) => (
                  <span
                    key={v}
                    className="inline-flex items-center gap-1 rounded bg-white px-2 py-0.5 text-xs border border-slate-200"
                  >
                    {v}
                    <button
                      type="button"
                      onClick={() => setField(field, selected.filter((x) => x !== v))}
                      className="text-red-600"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {remainingValues.length > 0 && (
                  <select
                    className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) setField(field, [...selected, e.target.value]);
                    }}
                  >
                    <option value="">+ value</option>
                    {remainingValues.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={() => removeField(field)}
                  className="ml-auto text-xs text-slate-500 hover:text-red-600"
                  title="Remove filter"
                >
                  remove
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface PivotCell {
  /** Aggregated numeric output. NaN if no data. */
  value: number;
  /** Number of source rows that contributed (used by avg + display). */
  count: number;
  /** Sum of source values — kept so avg can be recomputed on totals lines. */
  sum: number;
  /** Min/max trackers. */
  min: number;
  max: number;
}

function emptyCell(): PivotCell {
  return { value: NaN, count: 0, sum: 0, min: Infinity, max: -Infinity };
}

function recomputeCellValue(cell: PivotCell, agg: PivotAggregate): number {
  if (cell.count === 0) return NaN;
  switch (agg) {
    case 'sum':
      return cell.sum;
    case 'count':
      return cell.count;
    case 'avg':
      return cell.sum / cell.count;
    case 'min':
      return cell.min;
    case 'max':
      return cell.max;
  }
}

function fmtPivotValue(
  v: number,
  agg: PivotAggregate,
  valueCol: CustomReportColumn | undefined,
): string {
  if (!Number.isFinite(v)) return '—';
  if (agg === 'count') return String(Math.round(v));
  // Reuse the column's type to drive currency formatting.
  if (valueCol?.type === 'currency') {
    if (valueCol.id.startsWith('balance_')) return formatBalance(v);
    return formatTransaction(v);
  }
  // Round non-currency numerics for readability.
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

/** Stringify a row's value for a given column id, used both for grouping
 *  and filtering. Null/undefined render as the em-dash sentinel. */
function cellKey(row: Record<string, unknown>, fieldId: string): string {
  const v = row[fieldId];
  return v == null ? '—' : String(v);
}

const SEP = ' › '; // " › " — used to join multi-dim keys for stable lookup
const SINGLE_COL = '__value__';

interface BuiltPivot {
  matrix: Map<string, Map<string, PivotCell>>;
  rowTuples: [string, string[]][];
  colTuples: [string, string[]][];
  rowTotals: Map<string, PivotCell>;
  colTotals: Map<string, PivotCell>;
  grandTotal: PivotCell;
  droppedByFilter: number;
}

/** Serialize the rendered pivot matrix to an AOA suitable for SheetJS or
 *  CSV. Mirrors the on-screen layout: multi-row column header (one row per
 *  col-dim level), per-row row-dim cells, aggregated values, row totals,
 *  and a final totals row. Cell values are emitted as raw numbers so Excel
 *  treats them as numeric (cell formatting in Excel can apply currency). */
function pivotToAoa({
  data,
  config,
  rowCols,
  colCols,
  valueCol,
}: {
  data: CustomReportPreviewResponse;
  config: PivotConfig;
  rowCols: CustomReportColumn[];
  colCols: CustomReportColumn[];
  valueCol: CustomReportColumn | undefined;
}): unknown[][] {
  const built = buildPivotMatrix(data.data, config, colCols.length > 0, valueCol);
  const aoa: unknown[][] = [];
  const rowDimCount = Math.max(1, rowCols.length);

  if (colCols.length > 0) {
    // One header row per col-dim level. Row-dim labels live on the bottom
    // header row only; higher levels emit blanks for those cells.
    for (let level = 0; level < colCols.length; level++) {
      const isLast = level === colCols.length - 1;
      const row: unknown[] = [];
      for (let i = 0; i < rowDimCount; i++) {
        row.push(isLast ? rowCols[i]?.label ?? '' : '');
      }
      for (const [, ctuple] of built.colTuples) {
        row.push(ctuple[level] ?? '');
      }
      row.push(isLast ? 'Total' : '');
      aoa.push(row);
    }
  } else {
    const row: unknown[] = rowCols.map((c) => c.label);
    if (rowCols.length === 0) row.push('');
    row.push(valueCol?.label ?? 'Count');
    row.push('Total');
    aoa.push(row);
  }

  for (const [rk, rtuple] of built.rowTuples) {
    const row: unknown[] = [];
    for (let i = 0; i < rowDimCount; i++) row.push(rtuple[i] ?? '');
    for (const [ck] of built.colTuples) {
      const cell = built.matrix.get(rk)?.get(ck);
      const v = cell ? recomputeCellValue(cell, config.aggregate) : NaN;
      row.push(Number.isFinite(v) ? v : '');
    }
    const rowTot = built.rowTotals.get(rk);
    const tv = rowTot ? recomputeCellValue(rowTot, config.aggregate) : NaN;
    row.push(Number.isFinite(tv) ? tv : '');
    aoa.push(row);
  }

  // Totals row at the bottom.
  const totalsRow: unknown[] = ['Total'];
  for (let i = 1; i < rowDimCount; i++) totalsRow.push('');
  for (const [ck] of built.colTuples) {
    const colTot = built.colTotals.get(ck);
    const v = colTot ? recomputeCellValue(colTot, config.aggregate) : NaN;
    totalsRow.push(Number.isFinite(v) ? v : '');
  }
  const grand = recomputeCellValue(built.grandTotal, config.aggregate);
  totalsRow.push(Number.isFinite(grand) ? grand : '');
  aoa.push(totalsRow);

  return aoa;
}

/** Pure aggregation pass — takes the (already-fetched) preview rows and a
 *  pivot config, returns the aggregated matrix + row/col tuples + totals.
 *  Called from PivotTable for rendering and from the Export handler. */
function buildPivotMatrix(
  rows: Record<string, unknown>[],
  config: PivotConfig,
  hasColDims: boolean,
  valueCol: CustomReportColumn | undefined,
): BuiltPivot {
  const m = new Map<string, Map<string, PivotCell>>();
  const rowMap = new Map<string, string[]>();
  const colMap = new Map<string, string[]>();
  const rowTotalsMap = new Map<string, PivotCell>();
  const colTotalsMap = new Map<string, PivotCell>();
  const grand = emptyCell();
  let dropped = 0;

  const accept = (cell: PivotCell, n: number) => {
    cell.count += 1;
    cell.sum += n;
    if (n < cell.min) cell.min = n;
    if (n > cell.max) cell.max = n;
  };
  const acceptCount = (cell: PivotCell) => {
    cell.count += 1;
    cell.sum += 1;
  };

  for (const row of rows) {
    let pass = true;
    for (const [field, allowed] of Object.entries(config.filters)) {
      if (allowed.length === 0) continue;
      if (!allowed.includes(cellKey(row, field))) {
        pass = false;
        break;
      }
    }
    if (!pass) {
      dropped += 1;
      continue;
    }

    const rowTuple = config.rowFields.map((f) => cellKey(row, f));
    const colTuple = config.colFields.map((f) => cellKey(row, f));
    const rk = rowTuple.join(SEP) || '__all__';
    const ck = hasColDims ? colTuple.join(SEP) : SINGLE_COL;

    const raw =
      config.aggregate === 'count' ? null : valueCol ? row[valueCol.id] : null;
    const num =
      config.aggregate === 'count' ? 1 : raw == null ? null : Number(raw);
    if (config.aggregate !== 'count' && (num == null || !Number.isFinite(num))) continue;

    if (!rowMap.has(rk)) rowMap.set(rk, rowTuple);
    if (!colMap.has(ck)) colMap.set(ck, colTuple);

    let inner = m.get(rk);
    if (!inner) {
      inner = new Map();
      m.set(rk, inner);
    }
    let cell = inner.get(ck);
    if (!cell) {
      cell = emptyCell();
      inner.set(ck, cell);
    }

    let rowTot = rowTotalsMap.get(rk);
    if (!rowTot) {
      rowTot = emptyCell();
      rowTotalsMap.set(rk, rowTot);
    }
    let colTot = colTotalsMap.get(ck);
    if (!colTot) {
      colTot = emptyCell();
      colTotalsMap.set(ck, colTot);
    }

    if (config.aggregate === 'count') {
      acceptCount(cell);
      acceptCount(rowTot);
      acceptCount(colTot);
      acceptCount(grand);
    } else {
      accept(cell, num as number);
      accept(rowTot, num as number);
      accept(colTot, num as number);
      accept(grand, num as number);
    }
  }

  const rowEntries = [...rowMap.entries()].sort(([, a], [, b]) =>
    a.join(SEP).localeCompare(b.join(SEP)),
  );
  const colEntries = hasColDims
    ? [...colMap.entries()].sort(([, a], [, b]) =>
        a.join(SEP).localeCompare(b.join(SEP)),
      )
    : ([[SINGLE_COL, [] as string[]]] as [string, string[]][]);

  return {
    matrix: m,
    rowTuples: rowEntries as [string, string[]][],
    colTuples: colEntries,
    rowTotals: rowTotalsMap,
    colTotals: colTotalsMap,
    grandTotal: grand,
    droppedByFilter: dropped,
  };
}

function PivotTable({
  data,
  config,
}: {
  data: CustomReportPreviewResponse;
  config: PivotConfig;
}) {
  const rowCols = config.rowFields
    .map((id) => data.columns.find((c) => c.id === id))
    .filter((c): c is CustomReportColumn => Boolean(c));
  const colCols = config.colFields
    .map((id) => data.columns.find((c) => c.id === id))
    .filter((c): c is CustomReportColumn => Boolean(c));
  const valueCol = data.columns.find((c) => c.id === config.valueField);

  const {
    matrix,
    rowTuples,
    colTuples,
    rowTotals,
    colTotals,
    grandTotal,
    droppedByFilter,
  } = useMemo(
    () => buildPivotMatrix(data.data, config, colCols.length > 0, valueCol),
    [data.data, config, colCols, valueCol],
  );

  if (rowCols.length === 0) {
    return <div className="text-sm text-red-600">Pick at least one Rows attribute.</div>;
  }
  if (config.aggregate !== 'count' && !valueCol) {
    return <div className="text-sm text-red-600">Pick a Values column.</div>;
  }
  if (rowTuples.length === 0) {
    return (
      <div className="text-sm text-slate-500">
        No rows to pivot — the current filters return no data.
        {droppedByFilter > 0 && ` (${droppedByFilter} excluded by attribute filters.)`}
      </div>
    );
  }

  const valueLabelForSingleCol = valueCol?.label ?? 'Count';

  return (
    <>
      {droppedByFilter > 0 && (
        <p className="mb-2 text-xs text-slate-500">
          {droppedByFilter} source row{droppedByFilter === 1 ? '' : 's'} excluded by
          attribute filters.
        </p>
      )}
      <table className="w-full text-sm">
        <thead>
          {/* Column-axis headers — one row per col-dim level. */}
          {colCols.length > 0 ? (
            colCols.map((cc, level) => (
              <tr key={cc.id} className="text-xs uppercase tracking-wide text-slate-500">
                {/* Reserve header columns for row-axis labels in the very
                    last col-axis row (so they sit just above the data
                    body). For higher levels we emit blank cells. */}
                {level < colCols.length - 1
                  ? rowCols.map((rc) => <th key={rc.id} className="py-0.5 px-2" />)
                  : rowCols.map((rc) => (
                      <th key={rc.id} className="py-1 px-2 text-left whitespace-nowrap">
                        {rc.label}
                      </th>
                    ))}
                {colTuples.map(([ckey, ctuple]) => (
                  <th
                    key={`${cc.id}::${ckey}`}
                    className="py-0.5 px-2 text-right whitespace-nowrap"
                  >
                    {ctuple[level] ?? '—'}
                  </th>
                ))}
                <th className="py-0.5 px-2 text-right border-l border-slate-300">
                  {level === colCols.length - 1 ? 'Total' : ''}
                </th>
              </tr>
            ))
          ) : (
            <tr className="text-xs uppercase tracking-wide text-slate-500">
              {rowCols.map((rc) => (
                <th key={rc.id} className="py-1 px-2 text-left whitespace-nowrap">
                  {rc.label}
                </th>
              ))}
              <th className="py-1 px-2 text-right whitespace-nowrap">
                {valueLabelForSingleCol}
              </th>
              <th className="py-1 px-2 text-right border-l border-slate-300">Total</th>
            </tr>
          )}
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rowTuples.map(([rk, rtuple]) => (
            <tr key={rk}>
              {rtuple.map((rv, i) => (
                <td
                  key={`${rk}::r${i}`}
                  className={`py-1 px-2 ${i === 0 ? 'font-medium' : 'text-slate-700'}`}
                >
                  {rv}
                </td>
              ))}
              {colTuples.map(([ck]) => {
                const cell = matrix.get(rk)?.get(ck);
                const v = cell ? recomputeCellValue(cell, config.aggregate) : NaN;
                return (
                  <td key={ck} className="py-1 px-2 text-right tabular-nums">
                    {fmtPivotValue(v, config.aggregate, valueCol)}
                  </td>
                );
              })}
              <td className="py-1 px-2 text-right tabular-nums border-l border-slate-200 font-medium">
                {fmtPivotValue(
                  recomputeCellValue(rowTotals.get(rk) ?? emptyCell(), config.aggregate),
                  config.aggregate,
                  valueCol,
                )}
              </td>
            </tr>
          ))}
          <tr className="border-t-2 border-slate-300 font-semibold">
            <td className="py-1 px-2" colSpan={Math.max(1, rowCols.length)}>
              Total
            </td>
            {colTuples.map(([ck]) => (
              <td key={ck} className="py-1 px-2 text-right tabular-nums">
                {fmtPivotValue(
                  recomputeCellValue(colTotals.get(ck) ?? emptyCell(), config.aggregate),
                  config.aggregate,
                  valueCol,
                )}
              </td>
            ))}
            <td className="py-1 px-2 text-right tabular-nums border-l border-slate-300">
              {fmtPivotValue(
                recomputeCellValue(grandTotal, config.aggregate),
                config.aggregate,
                valueCol,
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
}

// ---------------------------------------------------------------------------
// Column selector
// ---------------------------------------------------------------------------

function ColumnSelector({
  all,
  state,
  onChange,
}: {
  all: CustomReportColumn[];
  state: BuilderState;
  onChange: (cols: string[]) => void;
}) {
  const required = BALANCE_VARIANT_BY_SOURCE[state.balance_source];
  const isLocked = (id: string) => id === REQUIRED_COL || id === required;
  const grouped = useMemo(() => {
    const m = new Map<string, CustomReportColumn[]>();
    for (const c of all) {
      let arr = m.get(c.group);
      if (!arr) {
        arr = [];
        m.set(c.group, arr);
      }
      arr.push(c);
    }
    return Array.from(m.entries());
  }, [all]);
  const selected = new Set(state.columns);

  function toggle(id: string) {
    if (isLocked(id)) return;
    const next = selected.has(id)
      ? state.columns.filter((c) => c !== id)
      : [...state.columns, id];
    onChange(next);
  }

  function move(id: string, dir: -1 | 1) {
    const idx = state.columns.indexOf(id);
    if (idx < 0) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= state.columns.length) return;
    const next = [...state.columns];
    [next[idx], next[swap]] = [next[swap]!, next[idx]!];
    onChange(next);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Columns</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Selected (in order)</div>
          <ul className="space-y-1 text-sm">
            {state.columns.map((id) => {
              const col = all.find((c) => c.id === id);
              return (
                <li key={id} className="flex items-center justify-between rounded border border-slate-200 px-2 py-1">
                  <span>
                    {col?.label ?? id}
                    {isLocked(id) && <span className="ml-2 text-xs text-slate-500">locked</span>}
                  </span>
                  {!isLocked(id) && (
                    <span className="space-x-1 text-xs">
                      <button onClick={() => move(id, -1)}>↑</button>
                      <button onClick={() => move(id, +1)}>↓</button>
                      <button onClick={() => toggle(id)} className="text-red-600">
                        ×
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">All attributes</div>
          {grouped.map(([group, items]) => (
            <div key={group} className="mb-2">
              <div className="text-xs font-semibold text-slate-700">{group}</div>
              <ul className="space-y-0.5 text-sm">
                {items.map((c) => {
                  const checked = selected.has(c.id);
                  // Hide the OTHER balance variants — only show the matching one.
                  if (
                    c.balance_variant &&
                    c.balance_variant !== state.balance_source
                  ) {
                    return null;
                  }
                  return (
                    <li key={c.id}>
                      <label className={`flex items-center ${isLocked(c.id) ? 'opacity-70' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isLocked(c.id)}
                          onChange={() => toggle(c.id)}
                          className="mr-2"
                        />
                        {c.label}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Filter builder (mirrors Accounts list facets)
// ---------------------------------------------------------------------------

function FilterBuilder({
  state,
  onChange,
}: {
  state: CustomReportFilters;
  onChange: (filters: CustomReportFilters) => void;
}) {
  function set<K extends keyof CustomReportFilters>(key: K, value: CustomReportFilters[K]): void {
    const next = { ...state, [key]: value };
    if (value === null || value === undefined || value === '') delete next[key];
    onChange(next);
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Filters</CardTitle>
      </CardHeader>
      <CardBody className="space-y-2 text-sm">
        <label className="block">
          Search display_name
          <input
            value={state.q ?? ''}
            onChange={(e) => set('q', e.target.value || null)}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
          />
        </label>
        <fieldset>
          <legend className="text-xs uppercase tracking-wide text-slate-500">Account status</legend>
          {(['active', 'suppressed', 'closed', 'converted_personal'] as const).map((opt) => (
            <label key={opt} className="mr-2 inline-flex items-center text-xs">
              <input
                type="checkbox"
                checked={
                  Array.isArray(state.account_status)
                    ? state.account_status.includes(opt)
                    : state.account_status === opt
                }
                onChange={(e) => {
                  const cur = Array.isArray(state.account_status)
                    ? state.account_status
                    : state.account_status
                      ? [state.account_status]
                      : [];
                  const next = e.target.checked ? [...cur, opt] : cur.filter((s) => s !== opt);
                  set('account_status', next.length ? next : null);
                }}
                className="mr-1"
              />
              {opt}
            </label>
          ))}
        </fieldset>
        <fieldset>
          <legend className="text-xs uppercase tracking-wide text-slate-500">Tiller status</legend>
          {(['integrated', 'manual', 'login_issues', 'new'] as const).map((opt) => (
            <label key={opt} className="mr-2 inline-flex items-center text-xs">
              <input
                type="checkbox"
                checked={
                  Array.isArray(state.tiller_status)
                    ? state.tiller_status.includes(opt)
                    : state.tiller_status === opt
                }
                onChange={(e) => {
                  const cur = Array.isArray(state.tiller_status)
                    ? state.tiller_status
                    : state.tiller_status
                      ? [state.tiller_status]
                      : [];
                  const next = e.target.checked ? [...cur, opt] : cur.filter((s) => s !== opt);
                  set('tiller_status', next.length ? next : null);
                }}
                className="mr-1"
              />
              {opt}
            </label>
          ))}
        </fieldset>
        <label className="block">
          Opened from
          <input
            type="date"
            value={state.opened_from ?? ''}
            onChange={(e) => set('opened_from', e.target.value || null)}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
          />
        </label>
        <label className="block">
          Opened to
          <input
            type="date"
            value={state.opened_to ?? ''}
            onChange={(e) => set('opened_to', e.target.value || null)}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
          />
        </label>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Balance source picker
// ---------------------------------------------------------------------------

function BalanceSourcePicker({
  state,
  onChange,
}: {
  state: BuilderState;
  onChange: (next: BuilderState) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Balance Source</CardTitle>
      </CardHeader>
      <CardBody className="space-y-2 text-sm">
        {(['current', 'last_month_end', 'as_of_period'] as const).map((opt) => (
          <label key={opt} className="block">
            <input
              type="radio"
              name="balance_source"
              checked={state.balance_source === opt}
              onChange={() => onChange({ ...state, balance_source: opt })}
              className="mr-2"
            />
            {opt === 'current'
              ? 'Current (latest balance per active account)'
              : opt === 'last_month_end'
                ? 'Last Month-End (latest frozen snapshot period)'
                : 'As-of Period (pick a YYYY-MM)'}
          </label>
        ))}
        {state.balance_source === 'as_of_period' && (
          <input
            type="month"
            value={state.default_as_of_period ?? ''}
            onChange={(e) => onChange({ ...state, default_as_of_period: e.target.value || null })}
            className="rounded border border-slate-300 px-2 py-1"
          />
        )}
      </CardBody>
    </Card>
  );
}
